import React from 'react';
import { FixedSchedule } from './FixedSchedule';
import {
  DAY_BTN_ACTIVE,
  DAY_BTN_INACTIVE,
  getDayPills,
  WorkWeekValue,
} from './helpers';

const WEEK_OPTIONS = [1, 2, 3, 4] as const;
const WEEK_BTN_BASE =
  'tw-inline-flex tw-items-center tw-px-4 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-semibold tw-border tw-border-solid';

interface ReducedScheduleProps {
  onChange: (e: { value: WorkWeekValue }) => void;
  value?: WorkWeekValue;
  onBlur?: () => void;
  processedProps?: boolean;
}

export const ReducedScheduleHours = ({
  onChange,
  value,
  onBlur,
  processedProps,
}: ReducedScheduleProps) => {
  const initLength = (() => {
    const n = value?.weeks?.length ?? 0;
    return n >= 1 && n <= 4 ? n : null;
  })();

  const [weekLength, setWeekLength] = React.useState<number | null>(initLength);
  const [weeksData, setWeeksData] = React.useState<string[][]>(
    value?.weeks ?? [],
  );
  const weekRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  // Tracks the last value we emitted so incoming echoes of our own value
  // don't clobber in-progress input during re-hydration.
  const lastEmittedRef = React.useRef<string | undefined>(undefined);

  // Re-initialise state when the incoming form value changes, e.g. when
  // returning to a form that has hydrated previously saved answers.
  React.useEffect(() => {
    if (!processedProps) return;
    const incoming = JSON.stringify(value?.weeks ?? null);
    // Skip the echo of the value we just emitted ourselves.
    if (incoming === lastEmittedRef.current) return;
    const n = value?.weeks?.length ?? 0;
    const newLength = n >= 1 && n <= 4 ? n : null;
    setWeekLength(newLength);
    setWeeksData(value?.weeks ?? []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps, value]);

  React.useEffect(() => {
    if (weekLength === null) return;
    const weeks = Array.from(
      { length: weekLength },
      (_, i) => weeksData[i] ?? new Array<string>(7).fill('00:00'),
    );
    lastEmittedRef.current = JSON.stringify(weeks);
    onChange({ value: { weeks } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeksData, weekLength]);

  // Grand total across all weeks.
  const grandTotalMins = Array.from(
    { length: weekLength ?? 0 },
    (_, i) => weeksData[i] ?? [],
  ).reduce(
    (weekAcc, week) =>
      weekAcc +
      week.reduce((dayAcc, t) => {
        const [h, m] = (t ?? '00:00').split(':');
        return (
          dayAcc + (Number.parseInt(h) || 0) * 60 + (Number.parseInt(m) || 0)
        );
      }, 0),
    0,
  );
  const grandTotalH = Math.floor(grandTotalMins / 60);
  const grandTotalM = grandTotalMins % 60;
  const grandTotalStr =
    grandTotalM > 0
      ? `${grandTotalH} hrs ${grandTotalM} mins`
      : `${grandTotalH} hrs`;

  const handleReducedHoursSelect = (n: number) => {
    setWeekLength(n);
    setWeeksData((prev) =>
      Array.from(
        { length: n },
        (_, i) => prev[i] ?? new Array<string>(7).fill('00:00'),
      ),
    );
  };

  const handleWeekChange =
    (weekIndex: number) => (e: { value: WorkWeekValue }) => {
      setWeeksData((prev) => {
        const next = [...prev];
        while (next.length <= weekIndex) {
          next.push(new Array<string>(7).fill('00:00'));
        }
        next[weekIndex] = e.value.weeks[0];
        return next;
      });
    };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id="form-reduced"
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0 tw-bg-white tw-rounded-[8px] tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)]"
      onBlur={handleBlur}
    >
      <div className="tw-px-5 tw-pt-6 tw-pb-5">
        <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-3">
          Select up to 4 weeks that you wish to reduce your schedule.
        </p>
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
          {WEEK_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              id={`btn-reduced-${n}`}
              onClick={() => handleReducedHoursSelect(n)}
              className={`${WEEK_BTN_BASE} ${
                weekLength === n ? DAY_BTN_ACTIVE : DAY_BTN_INACTIVE
              }`}
            >
              {n} week{n > 1 ? 's' : ''}
            </button>
          ))}
        </div>
        <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-2">
          Starting with the first reduced week, please list the days, hours and
          minutes that you will be reducing your schedule by.
        </p>
      </div>

      {/* Per-week schedule cards*/}
      {weekLength !== null &&
        Array.from({ length: weekLength }, (_, i) => (
          <div
            key={i}
            ref={(el) => {
              weekRefs.current[i] = el;
            }}
            id={`reduced-week-${i}`}
            className="tw-border-0 tw-border-t tw-border-solid tw-border-t-unm-mixed"
          >
            {/* Week header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-px-5 tw-pt-5 tw-pb-0">
              <div>
                <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-0">
                  Week {i + 1}
                </p>
              </div>
            </div>

            <FixedSchedule
              embedded
              processedProps={processedProps}
              onChange={handleWeekChange(i)}
              value={{
                weeks: [weeksData[i] ?? new Array<string>(7).fill('00:00')],
              }}
            />
          </div>
        ))}

      {/* Summary*/}
      {weekLength !== null && (
        <div className="tw-border-0 tw-border-t tw-border-solid tw-border-t-unm-mixed">
          <div
            id="reduced-summary"
            className="tw-border tw-rounded-[8px] tw-shadow-[0_1px_4px_rgba(0,0,0,0.12)] tw-mx-5 tw-my-5 tw-px-5 tw-py-5"
          >
            <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-3">
              Summary of hours and minutes reduced.
            </p>
            <div className="tw-flex tw-flex-col tw-gap-4">
              {Array.from({ length: weekLength }, (_, i) => {
                const weekData =
                  weeksData[i] ?? new Array<string>(7).fill('00:00');
                const pills = getDayPills(weekData);
                let pillsContent: React.ReactNode;
                if (pills && pills.length > 0) {
                  pillsContent = pills.map((label) => (
                    <span
                      key={label}
                      className={`${WEEK_BTN_BASE} tw-bg-unm-bg-light tw-text-unm-adhoc2 tw-border-[#929292]`}
                    >
                      {label}
                    </span>
                  ));
                } else {
                  pillsContent = (
                    <span className="tw-opacity-60">No days selected</span>
                  );
                }
                return (
                  <div
                    key={i}
                    className="tw-flex tw-flex-col tw-gap-2 tw-text-sm tw-text-unm-adhoc2"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span className="tw-font-semibold">Week {i + 1}:</span>
                      <button
                        type="button"
                        onClick={() =>
                          weekRefs.current[i]?.scrollIntoView({
                            behavior: 'smooth',
                          })
                        }
                        className="tw-text-unm-interactive01-400 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-underline"
                      >
                        Go to week {i + 1}
                      </button>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {pillsContent}
                    </div>
                  </div>
                );
              })}
            </div>
            <p
              id="reduced-grand-total"
              className="tw-text-base tw-text-unm-adhoc2 tw-mt-4 tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-t-unm-mixed"
            >
              Total hours reduced: <strong>{grandTotalStr}</strong>
            </p>
          </div>
        </div>
      )}
    </fieldset>
  );
};
